// After App admin's AuthLayout (firstfund-app/admin/src/pages/auth/AuthLayout.tsx): the same two columns, the Hub's
// brand (teal, the Firstfund logo of the sidebar) and the Hub's own words.
import type { ReactNode } from 'react'
import { History, KeySquare, ShieldCheck } from 'lucide-react'

/**
 * Every sign-in step (password, 2-step code, new password, 2-step set-up). Left: the brand and the form.
 * Right: the brand panel with three highlights (hidden below lg).
 */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen w-full flex-col bg-slate-50 dark:bg-slate-950 lg:flex-row">
      {/* Left: the form */}
      <div className="flex flex-1 flex-col justify-center p-6 sm:p-8 lg:p-12">
        <div className="mx-auto w-full max-w-md" data-testid="sign-in">
          <div className="mb-8 inline-flex items-center gap-2.5">
            <img src="/brand/firstfund-logo-no-descriptor.svg" alt="Firstfund" className="block h-6 w-auto dark:hidden" />
            <img src="/brand/firstfund-logo-no-descriptor-reversed.svg" alt="Firstfund" className="hidden h-6 w-auto dark:block" />
            <span className="h-6 w-px bg-slate-300 dark:bg-slate-700" aria-hidden="true" />
            <div>
              <p className="text-sm font-bold leading-none text-default">Lender Hub</p>
              <p className="mt-0.5 text-[10px] leading-none text-muted">Firstfund · staff only</p>
            </div>
          </div>

          <h1 className="mb-1 text-2xl font-bold text-default sm:text-3xl">{title}</h1>
          {subtitle && <p className="mb-6 text-xs text-muted">{subtitle}</p>}

          {children}
        </div>
      </div>

      {/* Right: the brand panel (desktop only) */}
      <div className="relative hidden flex-1 overflow-hidden bg-gradient-to-br from-primary via-primary-dark to-charcoal lg:flex">
        <div className="absolute inset-0 opacity-20" aria-hidden="true">
          <div className="absolute left-10 top-10 h-32 w-32 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-20 right-10 h-48 w-48 rounded-full bg-teal-300 blur-3xl" />
          <div className="absolute right-1/3 top-1/2 h-24 w-24 rounded-full bg-amber-400 blur-2xl" />
        </div>

        <div className="relative flex flex-col justify-center px-12 text-white xl:px-20">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-teal-100">Firstfund Lender Hub</p>
          <h2 className="mb-3 text-3xl font-bold leading-tight xl:text-4xl">Every lender, its APIs and its journeys, in one place.</h2>
          <p className="mb-8 max-w-md text-sm text-teal-50/90">
            Lenders and products, their UAT and production APIs, the code that calls them, the MIS they send back, and the
            directory the website, Apply now and the portals read.
          </p>

          <div className="space-y-4">
            <Highlight icon={ShieldCheck} title="2-step sign-in" body="A password and a code from your authenticator app." />
            <Highlight icon={KeySquare} title="Lender keys stay here" body="UAT and production keys live only in the Hub, never in the pages or the logs." />
            <Highlight icon={History} title="Every change on record" body="Admin, editor, reviewer or analyst: each sees what the role needs, and every change is in the audit log." />
          </div>
        </div>
      </div>
    </div>
  )
}

function Highlight({ icon: Icon, title, body }: { icon: typeof ShieldCheck; title: string; body: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm">
        <Icon size={14} />
      </div>
      <div>
        <p className="mb-0.5 text-sm font-semibold">{title}</p>
        <p className="text-[11px] text-teal-50/80">{body}</p>
      </div>
    </div>
  )
}
